import { ArrowDown, ArrowUp } from "lucide-react";
import { useConfig, fmtDate, fmtPrice } from "@/lib/api";
import { StatusBadge, WeakBadge, ConfBadge, InterestBar, ContradictionBadge } from "@/components/badges";
import { useEventDrawer } from "@/components/EventDrawer";

const COLS = [
  ["status", "Statut"], ["date", "Date"], ["time", "Heure"], ["title", "Événement"], ["commune", "Commune"], ["venue", "Lieu"],
  ["category", "Typologie"], ["organizer", "Organisateur"], ["public", "Public"], ["price", "Tarif"], ["interest", "Intérêt"],
  ["confidence", "Confiance"], ["sources", "Sources"],
];
const SORTABLE = new Set(["date", "title", "commune", "venue", "category", "organizer", "price", "interest", "confidence", "sources"]);

export const EventTable = ({ items, sort, order, onSort, compact = false }) => {
  const { data: cfg } = useConfig();
  const { open } = useEventDrawer();
  const cols = compact ? COLS.filter(([k]) => ["status", "date", "time", "title", "commune", "interest", "confidence"].includes(k)) : COLS;
  return (
    <>
    <EventCards items={items} open={open} cfg={cfg} />
    <table className="dense-table w-full border-collapse hidden md:table" data-testid="events-table">
      <thead>
        <tr>
          {cols.map(([k, label]) => (
            <th key={k} data-testid={`col-${k}`} onClick={() => onSort && SORTABLE.has(k) && onSort(k)} className={onSort && SORTABLE.has(k) ? "cursor-pointer select-none hover:text-slate-900" : ""}>
              <span className="inline-flex items-center gap-1">{label}{sort === k && (order === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((e, i) => (
          <tr key={e.id} data-testid={`event-row-${i}`} onClick={() => open(e.id)} className={`cursor-pointer ${e.status === "ANNULÉ" ? "opacity-70" : ""}`}>
            {cols.map(([k]) => <td key={k} className={k === "title" ? "min-w-[220px]" : ""}>{cell(k, e, cfg)}</td>)}
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={cols.length} className="text-center !py-10 text-slate-400" data-testid="events-empty">Aucun événement ne correspond aux filtres.</td></tr>}
      </tbody>
    </table>
    </>
  );
};

const EventCards = ({ items, open, cfg }) => (
  <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800" data-testid="events-cards">
    {items.map((e, i) => (
      <button key={e.id} type="button" data-testid={`event-card-${i}`} onClick={() => open(e.id)}
        className={`w-full text-left px-3 py-2.5 active:bg-slate-50 dark:active:bg-slate-800 ${e.status === "ANNULÉ" ? "opacity-70" : ""}`}>
        <div className="flex items-center gap-1.5 flex-wrap">
          {cell("status", e, cfg)}
          <span className="font-mono text-[11px] text-slate-600 dark:text-slate-300">{cell("date", e, cfg)} {e.start_time || ""}</span>
          <span className="ml-auto flex items-center gap-1.5">{cell("confidence", e, cfg)}</span>
        </div>
        <div className="mt-1 text-sm font-semibold leading-snug">{e.title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{[e.commune_name, e.venue_name].filter(Boolean).join(" · ")}</div>
        <div className="mt-1 flex items-center gap-2 text-[11px]">
          <span className="truncate">{cell("category", e, cfg)}</span>
          <span className="ml-auto w-20 shrink-0">{cell("interest", e, cfg)}</span>
        </div>
      </button>
    ))}
    {items.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">Aucun événement ne correspond aux filtres.</div>}
  </div>
);

function cell(k, e, cfg) {
  switch (k) {
    case "status": return <div className="flex flex-col gap-0.5 items-start"><StatusBadge status={e.status} />{e.weak_signal && <WeakBadge />}{e.has_contradiction && <ContradictionBadge />}</div>;
    case "date": return <span className="font-mono whitespace-nowrap">{fmtDate(e.next_date || e.start_date)}{e.multi_day && <span className="text-slate-400"> →{fmtDate(e.end_date, false)}</span>}</span>;
    case "time": return <span className={`font-mono ${e.start_time ? "" : "text-slate-400 text-[10px]"}`}>{e.start_time || "INCONNUE"}</span>;
    case "title": return <div><div className={`font-medium text-slate-900 dark:text-slate-100 ${e.status === "ANNULÉ" ? "line-through" : ""}`}>{e.title}{(e.locked_fields || []).length > 0 && <span data-testid={`manual-flag-${e.id}`} title="Modifié manuellement" className="ml-1.5 align-middle text-[9px] font-mono font-bold bg-blue-700 text-white rounded-[3px] px-1">MANUEL</span>}</div>{e.recurrence && <div className="text-[10px] text-slate-400">{e.recurrence.replace("_", " ")}</div>}</div>;
    case "commune": return e.commune_name;
    case "venue": return e.venue_name || <span className="text-slate-400 text-[10px] font-mono">INCONNU</span>;
    case "category": return <div className="flex flex-wrap gap-0.5">{(e.categories || []).slice(0, 2).map((c) => <span key={c} className={`text-[10px] px-1 rounded-[3px] border ${c.startsWith("ECO") ? "border-blue-300 text-blue-800 dark:text-blue-300" : "border-slate-200 dark:border-slate-700"}`}>{cfg?.categories?.[c] || c}</span>)}</div>;
    case "organizer": return e.organizer_name || <span className="text-slate-400 text-[10px] font-mono">INCONNU</span>;
    case "public": return <span className="text-[11px]">{(e.audiences || []).slice(0, 2).map((a) => cfg?.audiences?.[a] || a).join(", ") || (e.is_professional ? "Professionnels" : "—")}</span>;
    case "price": return <span className={`font-mono text-[11px] whitespace-nowrap ${fmtPrice(e) === "INCONNU" ? "text-slate-400 text-[10px]" : ""}`}>{fmtPrice(e)}</span>;
    case "interest": return <InterestBar value={e.interest} />;
    case "confidence": return <ConfBadge value={e.confidence} />;
    case "sources": return <span className="font-mono">{e.sources_count}</span>;
    default: return null;
  }
}
